'use client';

import { c as _c } from "react/compiler-runtime";
import type { UIEvent } from 'react';
import { Button, type ButtonProps } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { IconPlayCircle } from '@/app/components/icons/audio-video/icon-play-circle';
import { Container } from '@/app/components/layouts/container';
import { Image } from '@/app/components/media/image';
import { generateSources } from '@/app/components/media/image/generate-sources';
import { type ImageProps } from '@/app/components/media/image/types';
import { Video } from '@/app/components/media/video';
import { Butterfly } from '@/app/cv/components/sections/home/hero-banner/item/butterfly';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { safeCall } from '@/app/utils/safeCall';
import { handleSectionHrefClick } from '@/app/utils/scrollToSectionById';
type HeroBannerImage = Omit<ImageProps, 'aspectRatio'>;
export interface SectionHeroBannerItemProps {
  title: string;
  image: HeroBannerImage;
  imageDesktop: HeroBannerImage;
  video?: string;
  videoDesktop?: string;
  logo?: HeroBannerImage;
  logoDesktop?: HeroBannerImage;
  decorate?: HeroBannerImage;
  youtube?: {
    label: string;
    url: string;
  };
  buttons: ButtonProps[];
  index?: number;
  priority?: boolean;
  /** When true, video is paused. Controlled by parent (e.g. only active slide plays). */
  paused?: boolean;
  /** When false, pausing does not seek to 0 (e.g. while a YouTube modal is open). */
  rewindWhenPaused?: boolean;
  preventLoadVideo?: boolean;
  onReady?: (e?: UIEvent<HTMLVideoElement>) => void;
  onProgress?: (currentTime: number, duration: number) => void;
  onVideoEnded?: () => void;
  /** Opens the hero-level YouTube iframe modal (parent owns overlay + pause). */
  onYoutubeClick?: (youtube: {
    label: string;
    url: string;
  }) => void;
}
export const SectionHeroBannerItem = t0 => {
  const $ = _c(59);
  const {
    title,
    image,
    imageDesktop,
    video,
    videoDesktop,
    logo,
    decorate,
    youtube,
    buttons,
    index,
    priority: t1,
    paused: t2,
    rewindWhenPaused: t3,
    preventLoadVideo,
    onReady,
    onProgress,
    onVideoEnded,
    onYoutubeClick
  } = t0;
  const priority = t1 === undefined ? false : t1;
  const paused = t2 === undefined ? false : t2;
  const rewindWhenPaused = t3 === undefined ? true : t3;
  const t4 = onReady ? false : !paused;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("absolute inset-0 z-0", "before:absolute before:top-0 before:z-10 before:h-50 before:w-full before:bg-linear-to-b before:from-black/25 before:to-transparent", "after:absolute after:bottom-0 after:z-10 after:h-75 after:w-full after:bg-linear-to-t after:from-black/25 after:to-transparent");
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const t6 = image?.src;
  const t7 = image?.alt;
  let t8;
  if ($[1] !== t6 || $[2] !== t7) {
    t8 = {
      src: t6,
      alt: t7,
      width: 600,
      height: 800
    };
    $[1] = t6;
    $[2] = t7;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const t9 = image?.src;
  const t10 = video ? 60 : undefined;
  let t11;
  if ($[4] !== imageDesktop?.src || $[5] !== t10 || $[6] !== t9 || $[7] !== videoDesktop) {
    t11 = generateSources("overall", {
      mobileSrc: t9,
      mobileOptions: {
        quality: t10
      },
      desktopSrc: imageDesktop?.src,
      desktopOptions: {
        quality: videoDesktop ? 60 : undefined
      }
    });
    $[4] = imageDesktop?.src;
    $[5] = t10;
    $[6] = t9;
    $[7] = videoDesktop;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  let t12;
  if ($[9] !== video || $[10] !== videoDesktop) {
    t12 = {
      mobile: video,
      desktop: videoDesktop
    };
    $[9] = video;
    $[10] = videoDesktop;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== t11 || $[13] !== t12 || $[14] !== t8) {
    t13 = {
      img: t8,
      responsives: t11,
      video: t12
    };
    $[12] = t11;
    $[13] = t12;
    $[14] = t8;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== onProgress || $[17] !== onReady || $[18] !== onVideoEnded || $[19] !== paused || $[20] !== preventLoadVideo || $[21] !== priority || $[22] !== rewindWhenPaused || $[23] !== t13 || $[24] !== t4) {
    t14 = <Video priority={priority} autoplay={t4} paused={paused} rewindWhenPaused={rewindWhenPaused} preventLoadVideo={preventLoadVideo} onReady={onReady} onProgress={onProgress} onVideoEnded={onVideoEnded} className={t5} sources={t13} />;
    $[16] = onProgress;
    $[17] = onReady;
    $[18] = onVideoEnded;
    $[19] = paused;
    $[20] = preventLoadVideo;
    $[21] = priority;
    $[22] = rewindWhenPaused;
    $[23] = t13;
    $[24] = t4;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn("xs:pb-19 pt-10 pb-17 sm:pt-14 sm:pb-23 lg:gap-y-6", "relative flex size-full flex-col items-center justify-between lg:items-start lg:justify-end");
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== decorate?.src || $[28] !== logo?.alt || $[29] !== logo?.height || $[30] !== logo?.src || $[31] !== logo?.width || $[32] !== priority) {
    t16 = !!logo?.src && <div className="relative mx-auto w-50 max-w-75 lg:mx-0 lg:w-75"><Image src={logo?.src || ""} alt={logo?.alt || "Logo"} width={logo?.width || 0} height={logo?.height || 0} aspectRatio="calculate" objectFit="contain" imageClassName="lg:object-left" priority={priority} />{!!decorate?.src && <Butterfly src={decorate?.src} />}</div>;
    $[27] = decorate?.src;
    $[28] = logo?.alt;
    $[29] = logo?.height;
    $[30] = logo?.src;
    $[31] = logo?.width;
    $[32] = priority;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== title) {
    t17 = <h2 className="text-sub-h2 font-noto-bold where-span:text-inline-wrap text-center text-white" data-test="banner-title" dangerouslySetInnerHTML={{
      __html: title
    }} />;
    $[34] = title;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t16 || $[37] !== t17) {
    t18 = <div className="flex flex-col items-center gap-y-4 lg:items-start">{t16}{t17}</div>;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== index || $[40] !== onYoutubeClick || $[41] !== youtube) {
    t19 = !!youtube && <Button iconBefore={<IconPlayCircle />} variant="tertiary" color="white" size="xl" onClick={() => safeCall(onYoutubeClick, youtube)} className="lg:absolute lg:right-0 lg:-bottom-3" {...createDataAttributes({
      test: `hero-banner-video-button-${index}`
    })} {...createDataGA({
      click: true,
      eventName: "hero-banner-video-button",
      group: `hero-banner-video-button-${index}`,
      url: youtube.url,
      text: youtube.label as string
    })}>{youtube.label}</Button>;
    $[39] = index;
    $[40] = onYoutubeClick;
    $[41] = youtube;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== buttons || $[44] !== index) {
    let t21;
    if ($[46] !== index) {
      t21 = (button, i) => <Button key={i} href={button.href} variant="secondary" color="white" className="w-full md:w-auto" aria-label={button.children as string} onClick={event => handleSectionHrefClick(button.href, event)} {...createDataAttributes({
        test: `hero-banner-cta-${index}`
      })} {...createDataGA({
        click: true,
        eventName: "hero-banner-cta",
        group: `hero-banner-cta-${index}`,
        url: button.href,
        text: button.children as string
      })}>{button.children}</Button>;
      $[46] = index;
      $[47] = t21;
    } else {
      t21 = $[47];
    }
    t20 = buttons.map(t21);
    $[43] = buttons;
    $[44] = index;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[48] !== t20) {
    t21 = <ButtonGroup className="w-full md:justify-center lg:justify-start">{t20}</ButtonGroup>;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t19 || $[51] !== t21) {
    t22 = <div className="relative flex w-full flex-col justify-center lg:items-end lg:gap-3">{t19}{t21}</div>;
    $[50] = t19;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t18 || $[54] !== t22) {
    t23 = <Container className={t15}>{t18}{t22}</Container>;
    $[53] = t18;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t14 || $[57] !== t23) {
    t24 = <div className="relative size-full">{t14}{t23}</div>;
    $[56] = t14;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  return t24;
};